"use client";

import Link from "next/link";
import Image from '@/components/ui/MediaImage';
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePathname, useRouter } from "next/navigation";
import { AppRouterContext } from "next/dist/shared/lib/app-router-context.shared-runtime";

// SSR-safe router hook wrapper
// Backend'den render edildiğinde App Router context'i olmayabilir
// Hook'ları her zaman çağır (React kuralları) ama context yoksa fallback kullan
function useSafeRouter() {
    const routerContext = useContext(AppRouterContext);
    // Hook'u her zaman çağır (React kuralları)
    // Context yoksa hook hata fırlatacak, bu durumda null döndür
    try {
        const router = useRouter();
        return router;
    } catch (error) {
        // SSR ortamında router context yoksa null döndür
        if (typeof window === 'undefined' || !routerContext) {
            return null;
        }
        throw error;
    }
}

// SSR-safe pathname hook wrapper
function useSafePathname() {
    const routerContext = useContext(AppRouterContext);
    // Hook'u her zaman çağır (React kuralları)
    // Context yoksa hook hata fırlatacak, bu durumda null döndür
    try {
        const pathname = usePathname();
        return pathname;
    } catch (error) {
        // SSR ortamında router context yoksa null döndür
        if (typeof window === 'undefined' || !routerContext) {
            return null;
        }
        throw error;
    }
}
import ProfileMenuWrapper from "@/components/ui/ProfileMenuWrapper";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import clsx from "clsx";
import { getMenu } from "@/lib/api-helpers";
import { ensureMenuEntity, extractMenuItems } from "@/types/menu";
import type { MenuData, MenuItem } from "@/types/menu";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import type { Language } from "@/lib/language-client-utils";
import { setLanguageCookies, resolveLanguageFromPath } from "@/lib/language-client-utils";
import { useCustomerPanelUrlBuilder, useCustomerPanelTranslations } from "@/lib/customer-panel-utils";
import type { PageUrlMap } from "@/types/blog";
import { transformMediaUrl } from "@/lib/media-url-transformer";
import { nextDeterministicId } from "@/lib/monotonic-now";
import type { HeaderSocialItem } from "@/lib/header-top-utils";

export interface HeaderMenuClientProps {
    initialMenuData?: MenuData | MenuItem[] | null;
    initialError?: string | null;
    position?: string;
    initialLanguageCode?: string;
    contactInfo?: {
        address?: string;
        email?: string;
        phone?: string;
    };
    /** header_top menüsü — yalnızca md altı mobil drawer (üst bar gizli) */
    headerTopLinks?: MenuItem[];
    headerSocialLinks?: HeaderSocialItem[];
    languages?: Language[];
    defaultLanguageCode?: string;
    pageUrls?: PageUrlMap;
    translations?: Record<string, string>;
    initialCustomer?: import('@/services/CustomerApiService').Customer | null;
    /** Sunucuda laravel_session yoksa true; client /api/customer/me çağırmaz */
    noSessionOnServer?: boolean;
    /** General settings logo_tagline — header logo altı slogan */
    logoTagline?: string | null;
}

interface MenuItemType {
    id: string;
    label: string;
    href?: string;
    type: string;
    hasDropdown?: boolean;
    image?: string;
    menuStyle?: 'auto' | 'mega' | 'dropdown';
    categorySlug?: string;
    productType?: string;
    subItems?: MenuItemType[];
}

type MegaMenuFeaturedProduct = {
    id: number;
    name: string;
    slug: string;
    url?: string;
    metas?: { images?: string[] };
};

const DEFAULT_POSITION = "header";
const resolveMediaUrl = (value: string): string =>
    transformMediaUrl(value, null) || value;

/** Anasayfa: `/` veya `/en`, `/tr` (tek dil segmenti). Carousel overlay için. */
const isHomepagePath = (pathname: string | null | undefined): boolean => {
    if (!pathname) return false;
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) return true;
    return segments.length === 1 && /^[a-z]{2}$/i.test(segments[0]);
};

const generateFallbackId = (): string => nextDeterministicId('menu');

const ensureUniqueMenuIds = (items: MenuItemType[]): MenuItemType[] => {
    const seen = new Set<string>();
    return items.map((item, index) => {
        let id = item.id || `menu-${index}`;
        if (seen.has(id)) {
            id = `${id}-${index}`;
        }
        seen.add(id);
        const subItems = item.subItems?.length
            ? ensureUniqueMenuIds(item.subItems)
            : item.subItems;
        return subItems === item.subItems ? { ...item, id } : { ...item, id, subItems };
    });
};

const pickMenuItemImage = (item: MenuItem): string | undefined => {
    const anyItem = item as unknown as Record<string, unknown>;
    const candidates: unknown[] = [
        item.image,
        anyItem.image_url,
        anyItem.imageUrl,
        anyItem.thumbnail,
        anyItem.thumb,
        anyItem.cover,
        anyItem.cover_image,
        anyItem.coverImage,
        anyItem.photo,
        anyItem.media,
        anyItem.src,
    ];

    for (const c of candidates) {
        if (typeof c === 'string' && c.trim()) {
            return c;
        }
        if (c && typeof c === 'object') {
            const obj = c as Record<string, unknown>;
            if (typeof obj.url === 'string' && obj.url.trim()) {
                return obj.url;
            }
            if (typeof obj.src === 'string' && obj.src.trim()) {
                return obj.src;
            }
        }
    }
    return undefined;
};

const pickMenuItemSlug = (item: MenuItem): string | undefined => {
    const rec = item as Record<string, unknown>;
    if (typeof rec.slug === 'string' && rec.slug.trim()) {
        return rec.slug.trim();
    }
    return undefined;
};

const pickMenuProductType = (item: MenuItem): string | undefined => {
    const rec = item as Record<string, unknown>;
    const templateKey = rec.template_key ?? rec.templateKey;
    if (typeof templateKey === 'string' && templateKey.endsWith('-cat')) {
        return templateKey.slice(0, -4);
    }
    return undefined;
};

const featuredProductImage = (product: MegaMenuFeaturedProduct): string | undefined => {
    const first = product.metas?.images?.[0];
    return typeof first === 'string' && first.trim() ? first : undefined;
};

const transformMenuItems = (items: MenuItem[] = [], defaultLanguageCode?: string): MenuItemType[] => {
    return items.map((item) => {
        const possibleChildren = item.children ?? item.subItems ?? item.items;
        const children = Array.isArray(possibleChildren) ? possibleChildren : [];
        const hasSubItems = children.length > 0;

        // Fix: ensure id is always a string for MenuItemType
        let fallbackId: string;
        if (typeof item.id === "string") {
            fallbackId = item.id;
        } else if (typeof item.id === "number") {
            fallbackId = item.id.toString();
        } else if (typeof item.text === "string") {
            fallbackId = item.text.toLowerCase().replace(/\s+/g, "-");
        } else if (typeof item.label === "string") {
            fallbackId = item.label.toLowerCase().replace(/\s+/g, "-");
        } else {
            fallbackId = generateFallbackId();
        }

        // Backend'den gelen route alanını href'e dönüştür
        // Öncelik sırası: href > link > route
        let href: string | undefined = (item.href || item.link || item.route || undefined) as
            | string
            | undefined;

        // Href validation:
        // - Leaf node'larda boş href olamaz → '/'
        // - Dropdown/container node'larda (örn: "Yacht Routes") href boş olabilir → navigasyon yok
        if (!href || href.trim() === "" || href === "#") {
            href = hasSubItems ? undefined : "/";
        }
        
        // Href'in geçerli bir path olduğundan emin ol
        // Eğer tam URL ise (http/https ile başlıyorsa), sadece pathname'i al
        if (href && (href.startsWith("http://") || href.startsWith("https://"))) {
            try {
                const url = new URL(href);
                href = url.pathname + url.search;
            } catch {
                // URL parse edilemezse, href'i olduğu gibi bırak
            }
        }

        return {
            id: fallbackId,
            label: item.text || item.label || item.title || "",
            href: href,
            type: item.type || "main",
            hasDropdown: hasSubItems,
            image: pickMenuItemImage(item),
            menuStyle: ((item as unknown as Record<string, unknown>).menu_style ||
                (item as unknown as Record<string, unknown>).menuStyle ||
                'auto') as 'auto' | 'mega' | 'dropdown',
            categorySlug: pickMenuItemSlug(item),
            productType: pickMenuProductType(item),
            subItems: hasSubItems ? transformMenuItems(children, defaultLanguageCode) : undefined,
        };
    });
};

export default function HeaderMenuClient({
    initialMenuData,
    initialError = null,
    position = DEFAULT_POSITION,
    initialLanguageCode,
    contactInfo,
    headerTopLinks = [],
    headerSocialLinks = [],
    languages = [],
    defaultLanguageCode,
    pageUrls = {},
    translations = {},
    initialCustomer = null,
    noSessionOnServer = false,
    logoTagline = null,
}: HeaderMenuClientProps) {
    const resolvedDefaultLanguageCode = defaultLanguageCode || initialLanguageCode || 'en';
    if (!defaultLanguageCode && process.env.NODE_ENV === 'development') {
        console.error('HeaderMenuClient: defaultLanguageCode prop is missing, fallback will be used');
    }
    // SSR-safe router ve pathname hooks
    // Backend'den render edildiğinde App Router context'i olmayabilir
    const pathname = useSafePathname();
    const router = useSafeRouter();
    const isHomePage = isHomepagePath(pathname);
    const menuRef = useRef<HTMLDivElement>(null);
    const menuBarRef = useRef<HTMLDivElement>(null);
    const mainMenuCloseTimer = useRef<NodeJS.Timeout | null>(null);
    const subMenuCloseTimer = useRef<NodeJS.Timeout | null>(null);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [openSubMenuId, setOpenSubMenuId] = useState<string | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [expandedMobileMenuIds, setExpandedMobileMenuIds] = useState<Set<string>>(new Set());
    const [mobileLanguageOpen, setMobileLanguageOpen] = useState(false);
    const [mobileNavMounted, setMobileNavMounted] = useState(false);
    const [megaMenuTop, setMegaMenuTop] = useState(104);
    const [featuredProductsBySubMenu, setFeaturedProductsBySubMenu] = useState<
        Record<string, MegaMenuFeaturedProduct[]>
    >({});
    const [featuredLoadingSubMenuId, setFeaturedLoadingSubMenuId] = useState<string | null>(null);

    const prefersReduced = useReducedMotion();

    useEffect(() => {
        setMobileNavMounted(true);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const measureMenuBarBottom = () => {
            const menuBar = menuBarRef.current;
            if (!menuBar) return;
            setMegaMenuTop(Math.round(menuBar.getBoundingClientRect().bottom));
        };

        measureMenuBarBottom();

        const menuBar = menuBarRef.current;
        const resizeObserver = menuBar ? new ResizeObserver(measureMenuBarBottom) : null;
        if (menuBar && resizeObserver) {
            resizeObserver.observe(menuBar);
        }

        window.addEventListener('resize', measureMenuBarBottom);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', measureMenuBarBottom);
        };
    }, []);

    useEffect(() => {
        if (!openMenuId || typeof window === 'undefined') return;
        const menuBar = menuBarRef.current;
        if (menuBar) {
            setMegaMenuTop(Math.round(menuBar.getBoundingClientRect().bottom));
        }
    }, [openMenuId]);

    useEffect(() => {
        if (!mobileNavMounted) return;
        if (mobileMenuOpen) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = prev;
            };
        }
        return undefined;
    }, [mobileMenuOpen, mobileNavMounted]);

    // Dil seçimi için state ve fonksiyonlar
    const languageMap = useMemo(() => {
        const map = new Map<string, Language>();
        languages.forEach(lang => {
            map.set(lang.attributes.code, lang);
        });
        return map;
    }, [languages]);

    const [selectedLangCode, setSelectedLangCode] = useState<string>(() => {
        return resolveLanguageFromPath(pathname, languages) || initialLanguageCode || resolvedDefaultLanguageCode;
    });

    const selectedLang = languageMap.get(selectedLangCode) || languageMap.get(resolvedDefaultLanguageCode) || languages[0] || null;

    useEffect(() => {
        const codeFromPath = resolveLanguageFromPath(pathname, languages);
        if (codeFromPath && codeFromPath !== selectedLangCode) {
            setSelectedLangCode(codeFromPath);
            return;
        }
        if (!codeFromPath && selectedLangCode !== resolvedDefaultLanguageCode) {
            setSelectedLangCode(resolvedDefaultLanguageCode);
        }
    }, [pathname, selectedLangCode, resolvedDefaultLanguageCode, languages]);

    const handleLanguageChange = async (newLang: Language) => {
        const newLangCode = newLang.attributes.code;
        if (selectedLangCode === newLangCode) {
            setMobileLanguageOpen(false);
            return;
        }

        const currentPath = pathname || '/';
        const pathSegments = currentPath.split('/').filter(Boolean);

        if (pathSegments[0] && /^[a-z]{2}$/.test(pathSegments[0])) {
            pathSegments.shift();
        }

        const pathWithoutLang = pathSegments.length > 0 ? '/' + pathSegments.join('/') : '';
        const pageSlug = pathSegments.length > 0 ? pathSegments[0] : '';

        setLanguageCookies(newLangCode);

        let targetUrl: string;

        if (pathSegments.length === 0) {
            targetUrl = newLang.attributes.is_default ? '/' : `/${newLangCode}`;
        } else {
            try {
                const params = new URLSearchParams({
                    slug: pageSlug,
                    current_language: selectedLangCode,
                    target_language: newLangCode,
                });
                const res = await fetch(`/api/alternate-url?${params.toString()}`);
                const data = await res.json();
                if (data?.url) {
                    targetUrl = data.url;
                } else {
                    targetUrl = newLang.attributes.is_default ? pathWithoutLang : `/${newLangCode}${pathWithoutLang}`;
                }
            } catch {
                targetUrl = newLang.attributes.is_default ? pathWithoutLang : `/${newLangCode}${pathWithoutLang}`;
            }
        }

        setSelectedLangCode(newLangCode);
        setMobileLanguageOpen(false);
        setMobileMenuOpen(false);
        if (router) {
            router.push(targetUrl);
        } else if (typeof window !== 'undefined') {
            window.location.href = targetUrl;
        }
    };

    const initialItems = useMemo(
        () => ensureUniqueMenuIds(transformMenuItems(extractMenuItems(initialMenuData), resolvedDefaultLanguageCode)),
        [initialMenuData, resolvedDefaultLanguageCode]
    );

    const [menuItems, setMenuItems] = useState<MenuItemType[]>(initialItems);
    const [menuLoading, setMenuLoading] = useState(initialItems.length === 0 && !initialError);
    const [menuError, setMenuError] = useState<string | null>(initialError);
    
    // LanguageVariablesContext kullanarak tekrar eden istekleri önle
    const { getVariable } = useLanguageVariables();
    
    // Customer panel utilities - pageUrls yoksa fetch et
    const [authPageUrls, setAuthPageUrls] = useState<PageUrlMap>(pageUrls);
    
    // pageUrls prop'u değiştiğinde authPageUrls'i güncelle
    useEffect(() => {
        if (pageUrls && Object.keys(pageUrls).length > 0) {
            setAuthPageUrls(prev => ({ ...prev, ...pageUrls }));
        }
    }, [pageUrls]);
    
    useEffect(() => {
        // Eksik URL'leri kontrol et ve fetch et
        const missingUrls: string[] = [];
        
        // Login ve register URL'leri kontrol et
        if (!authPageUrls || !authPageUrls['login']) {
            missingUrls.push('login');
        }
        if (!authPageUrls || !authPageUrls['register']) {
            missingUrls.push('register');
        }
        
        // Customer panel URL'leri kontrol et (HeaderMenu'den gelmiş olabilir ama eksikse fetch et)
        const customerPanelKeys = [
            'customer',
            'customer-dashboard',
            'customer-profile',
            'customer-reservations',
            'customer-favorites',
            'customer-newsletter',
            'customer-settings',
        ];
        
        customerPanelKeys.forEach(key => {
            if (!authPageUrls || !authPageUrls[key]) {
                missingUrls.push(key);
            }
        });
        
        // Eksik URL'leri fetch et
        if (missingUrls.length > 0) {
            const fetchAuthUrls = async () => {
                try {
                    const { getPageUrls } = await import('@/lib/api-helpers');
                    const response = await getPageUrls(
                      selectedLangCode || resolvedDefaultLanguageCode,
                      missingUrls
                    );
                    if (response.data) {
                        setAuthPageUrls(prev => ({ ...prev, ...response.data }));
                    }
                } catch (error) {
                    // Silent fail - fallback URL'ler kullanılacak
                }
            };
            fetchAuthUrls();
        }
    }, [authPageUrls]);
    
    const buildUrl = useCustomerPanelUrlBuilder(authPageUrls, selectedLangCode, resolvedDefaultLanguageCode);
    const t = useCustomerPanelTranslations(translations);
    
    // Customer name yönetimi artık ProfileMenu içinde yapılıyor
    // HeaderMenuClient'te customer authentication gerekmiyor - ProfileMenuWrapper kendi provider'ı ile yönetiyor
    
    // Logout handler - artık ProfileMenu içinde yapılıyor

    const languageCode = useMemo(() => {
        const pathSegments = pathname?.split('/').filter(Boolean) || [];
        const firstSegment = pathSegments[0];

        if (firstSegment && /^[a-z]{2}$/.test(firstSegment)) {
            return firstSegment;
        }

        return undefined;
    }, [pathname]);

    // URL'den route parametresini çıkar (varsayılan dilde ilk, varsayılan olmayan dilde ikinci parametre)
    const currentRoute = useMemo(() => {
        if (!pathname) return null;
        const pathSegments = pathname.split('/').filter(Boolean);
        if (pathSegments.length === 0) return null;
        
        const firstSegment = pathSegments[0];
        const isLangCode = firstSegment && /^[a-z]{2}$/.test(firstSegment);
        
        // Eğer ilk segment dil kodu ise, ikinci segment route
        // Değilse, ilk segment route
        const routeSegment = isLangCode ? pathSegments[1] : pathSegments[0];
        return routeSegment || null;
    }, [pathname]);

    // Link href'inden route'u çıkar
    const getRouteFromHref = useCallback((href: string | undefined): string | null => {
        if (!href || href === '#') return null;
        
        // URL parse etmeyi dene
        let pathname: string;
        try {
            // Eğer href zaten pathname ise (örn: /tour, /en/tour)
            if (href.startsWith('/')) {
                pathname = href;
            } else if (typeof window !== 'undefined') {
                // Tam URL ise parse et
                const url = new URL(href, window.location.origin);
                pathname = url.pathname;
            } else {
                // SSR'da window yoksa, href'i direkt pathname olarak kullan
                pathname = href.startsWith('/') ? href : `/${href}`;
            }
        } catch {
            // URL parse edilemezse, direkt pathname olarak dene
            pathname = href.startsWith('/') ? href : `/${href}`;
        }
        
        const pathSegments = pathname.split('/').filter(Boolean);
        if (pathSegments.length === 0) return null;
        
        const firstSegment = pathSegments[0];
        const isLangCode = firstSegment && /^[a-z]{2}$/.test(firstSegment);
        
        const routeSegment = isLangCode ? pathSegments[1] : pathSegments[0];
        return routeSegment || null;
    }, []);

    useEffect(() => {
        const shouldFetch =
            (!initialItems.length && !initialMenuData) ||
            (languageCode && languageCode !== initialLanguageCode);

        if (!shouldFetch) {
            return;
        }

        let isMounted = true;

        async function fetchMenu() {
            try {
                setMenuLoading(true);
                setMenuError(null);

                const menuResponse = await getMenu(position, languageCode);

                if (!menuResponse || !menuResponse.data) {
                    throw new Error('Menü verisi bulunamadı');
                }

                const menuEntity = ensureMenuEntity(menuResponse.data);
                if (!menuEntity) {
                    throw new Error('Menü verisi bulunamadı');
                }
                const menuData = menuEntity?.attributes?.data;
                const items = extractMenuItems(menuData);

                if (isMounted) {
                    const transformedItems = ensureUniqueMenuIds(transformMenuItems(items, resolvedDefaultLanguageCode));
                    setMenuItems(transformedItems);
                }
            } catch (error: unknown) {
                console.error('Header menu yüklenemedi:', error);
                if (isMounted) {
                    const message = error instanceof Error ? error.message : 'Menü yüklenirken bir hata oluştu';
                    setMenuError(message);
                    if (!initialItems.length) {
                        setMenuItems([]);
                    }
                }
            } finally {
                if (isMounted) {
                    setMenuLoading(false);
                }
            }
        }

        fetchMenu();

        return () => {
            isMounted = false;
        };
    }, [initialItems, initialMenuData, initialLanguageCode, languageCode, position]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setOpenMenuId(null);
                setOpenSubMenuId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setOpenMenuId(null);
                setOpenSubMenuId(null);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    useEffect(() => {
        if (openMenuId || mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [openMenuId, mobileMenuOpen]);

    // Pathname değiştiğinde menüyü kapat
    useEffect(() => {
        setOpenMenuId(null);
        setOpenSubMenuId(null);
        setMobileMenuOpen(false);
        setExpandedMobileMenuIds(new Set());
        
        // Timer'ları temizle
        if (mainMenuCloseTimer.current) {
            clearTimeout(mainMenuCloseTimer.current);
            mainMenuCloseTimer.current = null;
        }
        if (subMenuCloseTimer.current) {
            clearTimeout(subMenuCloseTimer.current);
            subMenuCloseTimer.current = null;
        }
    }, [pathname]);

    useEffect(() => {
        return () => {
            if (mainMenuCloseTimer.current) {
                clearTimeout(mainMenuCloseTimer.current);
            }
            if (subMenuCloseTimer.current) {
                clearTimeout(subMenuCloseTimer.current);
            }
        };
    }, []);

    const handleMainMenuEnter = (menuId: string) => {
        if (mainMenuCloseTimer.current) {
            clearTimeout(mainMenuCloseTimer.current);
            mainMenuCloseTimer.current = null;
        }
        if (subMenuCloseTimer.current) {
            clearTimeout(subMenuCloseTimer.current);
            subMenuCloseTimer.current = null;
        }

        setOpenMenuId(menuId);

        const currentMenu = menuItems?.find(item => item.id === menuId);
        const firstSubItemWithChildren = currentMenu?.subItems?.find(
            subItem => subItem.subItems && subItem.subItems.length > 0
        );

        if (firstSubItemWithChildren) {
            setOpenSubMenuId(firstSubItemWithChildren.id);
        } else if (currentMenu?.subItems?.length) {
            setOpenSubMenuId(currentMenu.subItems[0].id);
        } else {
            setOpenSubMenuId(null);
        }
    };

    const handleSubMenuEnter = (subMenuId: string) => {
        if (subMenuCloseTimer.current) {
            clearTimeout(subMenuCloseTimer.current);
            subMenuCloseTimer.current = null;
        }

        setOpenSubMenuId(subMenuId);
    };

    const handleMenuContainerEnter = () => {
        if (mainMenuCloseTimer.current) {
            clearTimeout(mainMenuCloseTimer.current);
            mainMenuCloseTimer.current = null;
        }
        if (subMenuCloseTimer.current) {
            clearTimeout(subMenuCloseTimer.current);
            subMenuCloseTimer.current = null;
        }
    };

    const handleMenuContainerLeave = () => {
        const currentMenu = menuItems?.find(item => item.id === openMenuId);
        const hasActiveSubmenuWithItems = openSubMenuId &&
            currentMenu?.subItems?.find(item => item.id === openSubMenuId)?.subItems?.length;

        const delay = hasActiveSubmenuWithItems ? 300 : 500;

        mainMenuCloseTimer.current = setTimeout(() => {
            setOpenMenuId(null);
            setOpenSubMenuId(null);
        }, delay);
    };

    const activeMegaMenuLink = useMemo(() => {
        if (!openMenuId || !menuItems?.length) return null;
        const link = menuItems.find((item) => item.id === openMenuId);
        if (!link?.subItems?.length) return null;
        if (link.menuStyle === 'dropdown') {
            return null;
        }
        const hasNestedSubItems = link.subItems.some(
            (subItem) => subItem.subItems && subItem.subItems.length > 0,
        );
        return hasNestedSubItems ? link : null;
    }, [openMenuId, menuItems]);

    const activeMegaMenuStyle = activeMegaMenuLink?.menuStyle ?? 'auto';

    const activeMegaSubMenu = useMemo(() => {
        if (!openSubMenuId || !activeMegaMenuLink?.subItems?.length) return null;
        return activeMegaMenuLink.subItems.find((item) => item.id === openSubMenuId) ?? null;
    }, [openSubMenuId, activeMegaMenuLink]);

    useEffect(() => {
        if (!activeMegaSubMenu?.categorySlug) return;
        if (activeMegaSubMenu.subItems && activeMegaSubMenu.subItems.length > 0) return;
        const subMenuId = activeMegaSubMenu.id;
        if (featuredProductsBySubMenu[subMenuId]) return;

        let cancelled = false;
        const params = new URLSearchParams({
            language: selectedLangCode,
            featured_only: 'true',
            per_page: '6',
        });
        if (activeMegaSubMenu.productType) {
            params.set('product_type', activeMegaSubMenu.productType);
        }

        setFeaturedLoadingSubMenuId(subMenuId);
        fetch(
            `/api/categories/${encodeURIComponent(activeMegaSubMenu.categorySlug)}/products?${params.toString()}`,
        )
            .then(async (res) => {
                if (!res.ok) return [] as MegaMenuFeaturedProduct[];
                const json = (await res.json()) as { data?: MegaMenuFeaturedProduct[] };
                return Array.isArray(json.data) ? json.data : [];
            })
            .then((items) => {
                if (cancelled) return;
                setFeaturedProductsBySubMenu((prev) => ({ ...prev, [subMenuId]: items }));
            })
            .catch(() => {
                if (!cancelled) {
                    setFeaturedProductsBySubMenu((prev) => ({ ...prev, [subMenuId]: [] }));
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setFeaturedLoadingSubMenuId((current) => (current === subMenuId ? null : current));
                }
            });

        return () => {
            cancelled = true;
        };
    }, [activeMegaSubMenu, selectedLangCode]);

    const desktopMenuBackdrop =
        mobileNavMounted && openMenuId
            ? createPortal(
                  <AnimatePresence>
                      {openMenuId && (
                          <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.15 }}
                              className="fixed left-0 right-0 bottom-0 z-[109] bg-black/20 backdrop-blur-xl"
                              style={{ top: megaMenuTop }}
                              onClick={() => {
                                  setOpenMenuId(null);
                                  setOpenSubMenuId(null);
                              }}
                              aria-hidden
                          />
                      )}
                  </AnimatePresence>,
                  document.body,
              )
            : null;

    return (
        <>
            {desktopMenuBackdrop}
            <div
                ref={menuBarRef}
                onMouseEnter={handleMenuContainerEnter}
                onMouseLeave={handleMenuContainerLeave}
                className={clsx(
                    "relative z-[110] min-w-0 max-w-full overflow-x-clip px-3 font-sans sm:px-4 lg:overflow-visible",
                    isHomePage
                        ? "absolute left-0 right-0 top-full border-b border-black/5 bg-white/65 backdrop-blur-[2px]"
                        : "relative bg-white",
                )}
            >
                {/* Tek satır flex: logo | nav (ortada) | profil + mobil menü — grid+flex karışımı menüyü logonun üstüne bindiriyordu */}
                <div className="mx-auto grid w-full min-w-0 max-w-7xl grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2 py-3 sm:gap-x-3 sm:py-3.5 lg:gap-x-4 lg:py-4 2xl:max-w-[min(100%,1600px)] 2xl:gap-x-5">
                    <Link
                        href="/"
                        className="flex w-max max-w-[150px] shrink-0 flex-col items-stretch justify-self-start sm:max-w-[170px] lg:max-w-[190px] xl:max-w-[220px] 2xl:max-w-[250px]"
                    >
                        <img
                            src={resolveMediaUrl("/static/alsero-logo.png")}
                            alt="alsero-logo"
                            width={250}
                            height={40}
                            loading="lazy"
                            decoding="async"
                            className="h-8 w-auto max-w-full object-contain object-left sm:h-9 lg:h-10 xl:h-11"
                        />
                        {logoTagline ? (
                            <span className="mt-1 block w-full text-left text-[11px] font-medium leading-snug tracking-wide text-alsero-text-dark sm:text-xs xl:text-[13px]">
                                {logoTagline}
                            </span>
                        ) : null}
                    </Link>

                    <nav
                        ref={menuRef}
                        className="relative z-[120] hidden min-w-0 justify-self-center lg:flex lg:max-w-full lg:items-center lg:justify-center lg:overflow-visible"
                        aria-label="Main navigation"
                    >
                        {menuLoading ? (
                            <div className="flex items-center gap-2 text-alsero-text-dark">
                                <div className="w-4 h-4 border-2 border-alsero-first border-t-transparent rounded-full animate-spin" />
                                <span className="text-sm">{getVariable('header_menu_loading', 'Loading menu...')}</span>
                            </div>
                        ) : menuError ? (
                            <div className="flex items-center gap-2 text-red-600">
                                <span className="text-sm font-medium">⚠️ {menuError}</span>
                            </div>
                        ) : menuItems && menuItems.length > 0 ? (
                            <div className="flex w-max max-w-full flex-nowrap items-center justify-center gap-0.5 xl:gap-1 2xl:gap-1.5 mx-auto">
                            {menuItems.map((link, menuIndex) => {
                                const hasNestedSubItems = link.subItems?.some(subItem => subItem.subItems && subItem.subItems.length > 0);
                                const topMenuStyle = link.menuStyle ?? 'auto';
                                const useMegaChrome = hasNestedSubItems && topMenuStyle !== 'dropdown';
                                const linkRoute = getRouteFromHref(link.href);
                                const isActive = currentRoute && linkRoute && currentRoute === linkRoute;

                                return link.subItems && link.subItems.length > 0 ? (
                                    useMegaChrome ? (
                                        <div
                                            key={`${link.id}-${menuIndex}`}
                                            className="relative shrink-0"
                                            onMouseEnter={() => handleMainMenuEnter(link.id)}
                                        >
                                            {link.href ? (
                                                <Link
                                                    href={link.href}
                                                    className={clsx(
                                                        "font-medium text-sm hover:text-alsero-first px-2 py-2 rounded-lg transition-all ease-in-out relative group inline-flex shrink-0 items-center cursor-pointer whitespace-nowrap xl:text-[15px] xl:px-3 2xl:px-3.5",
                                                        (openMenuId === link.id || isActive) ? "text-alsero-first" : "text-alsero-text-dark"
                                                    )}
                                                    title={`${link.label} sayfasına git veya alt menüyü görüntüle`}
                                                    onMouseEnter={(e) => {
                                                        e.stopPropagation();
                                                        if (mainMenuCloseTimer.current) {
                                                            clearTimeout(mainMenuCloseTimer.current);
                                                            mainMenuCloseTimer.current = null;
                                                        }
                                                    }}
                                                >
                                                    <span className="relative z-10 flex items-center gap-1.5">
                                                        {link.label}
                                                        <svg
                                                            width="8"
                                                            height="5"
                                                            viewBox="0 0 8 5"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className={clsx(
                                                                "transition-transform duration-200 group-hover:fill-alsero-first",
                                                                (openMenuId === link.id || isActive) ? "rotate-180 fill-alsero-first" : "fill-alsero-text-dark"
                                                            )}
                                                            aria-hidden="true"
                                                            focusable="false"
                                                        >
                                                            <path d="M7.87213 0.417894L7.61008 0.138679C7.52761 0.0514798 7.41788 0.00344123 7.30061 0.00344123C7.18341 0.00344123 7.07355 0.0514798 6.99108 0.138679L4.00179 3.30193L1.00905 0.135169C0.926707 0.0479698 0.816852 0 0.699648 0C0.582444 0 0.472524 0.0479698 0.390117 0.135169L0.128001 0.412663C-0.042667 0.593118 -0.042667 0.887061 0.128001 1.06752L3.69122 4.85148C3.77356 4.93861 3.88328 5 4.00153 5H4.00289C4.12016 5 4.22989 4.93854 4.31223 4.85148L7.87213 1.07777C7.9546 0.99064 7.99987 0.871025 8 0.747006C8 0.622918 7.9546 0.504955 7.87213 0.417894Z"/>
                                                        </svg>
                                                    </span>
                                                    <div
                                                        className={clsx(
                                                            "absolute inset-0 rounded-lg bg-alsero-first group-hover:opacity-20 transition-opacity duration-300 ease-in-out pointer-events-none",
                                                            (openMenuId === link.id || isActive) ? "opacity-20" : "opacity-0"
                                                        )}
                                                    />
                                                </Link>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className={clsx(
                                                        "font-medium text-sm hover:text-alsero-first px-2 py-2 rounded-lg transition-all ease-in-out relative group inline-flex shrink-0 items-center cursor-pointer whitespace-nowrap xl:text-[15px] xl:px-3",
                                                        (openMenuId === link.id || isActive) ? "text-alsero-first" : "text-alsero-text-dark"
                                                    )}
                                                    title={`${link.label} alt menüyü görüntüle`}
                                                    onMouseEnter={(e) => {
                                                        e.stopPropagation();
                                                        if (mainMenuCloseTimer.current) {
                                                            clearTimeout(mainMenuCloseTimer.current);
                                                            mainMenuCloseTimer.current = null;
                                                        }
                                                    }}
                                                >
                                                    <span className="relative z-10 flex items-center gap-1.5">
                                                        {link.label}
                                                        <svg
                                                            width="8"
                                                            height="5"
                                                            viewBox="0 0 8 5"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className={clsx(
                                                                "transition-transform duration-200 group-hover:fill-alsero-first",
                                                                (openMenuId === link.id || isActive) ? "rotate-180 fill-alsero-first" : "fill-alsero-text-dark"
                                                            )}
                                                            aria-hidden="true"
                                                            focusable="false"
                                                        >
                                                            <path d="M7.87213 0.417894L7.61008 0.138679C7.52761 0.0514798 7.41788 0.00344123 7.30061 0.00344123C7.18341 0.00344123 7.07355 0.0514798 6.99108 0.138679L4.00179 3.30193L1.00905 0.135169C0.926707 0.0479698 0.816852 0 0.699648 0C0.582444 0 0.472524 0.0479698 0.390117 0.135169L0.128001 0.412663C-0.042667 0.593118 -0.042667 0.887061 0.128001 1.06752L3.69122 4.85148C3.77356 4.93861 3.88328 5 4.00153 5H4.00289C4.12016 5 4.22989 4.93854 4.31223 4.85148L7.87213 1.07777C7.9546 0.99064 7.99987 0.871025 8 0.747006C8 0.622918 7.9546 0.504955 7.87213 0.417894Z"/>
                                                        </svg>
                                                    </span>
                                                    <div
                                                        className={clsx(
                                                            "absolute inset-0 rounded-lg bg-alsero-first group-hover:opacity-20 transition-opacity duration-300 ease-in-out pointer-events-none",
                                                            (openMenuId === link.id || isActive) ? "opacity-20" : "opacity-0"
                                                        )}
                                                    />
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div
                                            key={`${link.id}-${menuIndex}`}
                                            className="relative shrink-0"
                                            onMouseEnter={() => handleMainMenuEnter(link.id)}
                                        >
                                            {link.href ? (
                                                <Link
                                                    href={link.href}
                                                    className={clsx(
                                                        "font-medium text-sm hover:text-alsero-first px-2 py-2 rounded-lg transition-all ease-in-out relative group inline-flex shrink-0 items-center cursor-pointer whitespace-nowrap xl:text-[15px] xl:px-3 2xl:px-3.5",
                                                        (openMenuId === link.id || isActive) ? "text-alsero-first" : "text-alsero-text-dark"
                                                    )}
                                                    title={`${link.label} sayfasına git veya alt menüyü görüntüle`}
                                                    onMouseEnter={(e) => {
                                                        e.stopPropagation();
                                                        if (mainMenuCloseTimer.current) {
                                                            clearTimeout(mainMenuCloseTimer.current);
                                                            mainMenuCloseTimer.current = null;
                                                        }
                                                    }}
                                                >
                                                    <span className="relative z-10 flex items-center gap-1.5">
                                                        {link.label}
                                                        <svg
                                                            width="8"
                                                            height="5"
                                                            viewBox="0 0 8 5"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className={clsx(
                                                                "transition-transform duration-200 group-hover:fill-alsero-first",
                                                                (openMenuId === link.id || isActive) ? "rotate-180 fill-alsero-first" : "fill-alsero-text-dark"
                                                            )}
                                                            aria-hidden="true"
                                                            focusable="false"
                                                        >
                                                            <path d="M7.87213 0.417894L7.61008 0.138679C7.52761 0.0514798 7.41788 0.00344123 7.30061 0.00344123C7.18341 0.00344123 7.07355 0.0514798 6.99108 0.138679L4.00179 3.30193L1.00905 0.135169C0.926707 0.0479698 0.816852 0 0.699648 0C0.582444 0 0.472524 0.0479698 0.390117 0.135169L0.128001 0.412663C-0.042667 0.593118 -0.042667 0.887061 0.128001 1.06752L3.69122 4.85148C3.77356 4.93861 3.88328 5 4.00153 5H4.00289C4.12016 5 4.22989 4.93854 4.31223 4.85148L7.87213 1.07777C7.9546 0.99064 7.99987 0.871025 8 0.747006C8 0.622918 7.9546 0.504955 7.87213 0.417894Z"/>
                                                        </svg>
                                                    </span>
                                                    <div
                                                        className={clsx(
                                                            "absolute inset-0 rounded-lg bg-alsero-first group-hover:opacity-20 transition-opacity duration-300 ease-in-out pointer-events-none",
                                                            (openMenuId === link.id || isActive) ? "opacity-20" : "opacity-0"
                                                        )}
                                                    />
                                                </Link>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className={clsx(
                                                        "font-medium text-sm hover:text-alsero-first px-2 py-2 rounded-lg transition-all ease-in-out relative group inline-flex shrink-0 items-center cursor-pointer whitespace-nowrap xl:text-[15px] xl:px-3",
                                                        (openMenuId === link.id || isActive) ? "text-alsero-first" : "text-alsero-text-dark"
                                                    )}
                                                    title={`${link.label} alt menüyü görüntüle`}
                                                    onMouseEnter={(e) => {
                                                        e.stopPropagation();
                                                        if (mainMenuCloseTimer.current) {
                                                            clearTimeout(mainMenuCloseTimer.current);
                                                            mainMenuCloseTimer.current = null;
                                                        }
                                                    }}
                                                >
                                                    <span className="relative z-10 flex items-center gap-1.5">
                                                        {link.label}
                                                        <svg
                                                            width="8"
                                                            height="5"
                                                            viewBox="0 0 8 5"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className={clsx(
                                                                "transition-transform duration-200 group-hover:fill-alsero-first",
                                                                (openMenuId === link.id || isActive) ? "rotate-180 fill-alsero-first" : "fill-alsero-text-dark"
                                                            )}
                                                            aria-hidden="true"
                                                            focusable="false"
                                                        >
                                                            <path d="M7.87213 0.417894L7.61008 0.138679C7.52761 0.0514798 7.41788 0.00344123 7.30061 0.00344123C7.18341 0.00344123 7.07355 0.0514798 6.99108 0.138679L4.00179 3.30193L1.00905 0.135169C0.926707 0.0479698 0.816852 0 0.699648 0C0.582444 0 0.472524 0.0479698 0.390117 0.135169L0.128001 0.412663C-0.042667 0.593118 -0.042667 0.887061 0.128001 1.06752L3.69122 4.85148C3.77356 4.93861 3.88328 5 4.00153 5H4.00289C4.12016 5 4.22989 4.93854 4.31223 4.85148L7.87213 1.07777C7.9546 0.99064 7.99987 0.871025 8 0.747006C8 0.622918 7.9546 0.504955 7.87213 0.417894Z"/>
                                                        </svg>
                                                    </span>
                                                    <div
                                                        className={clsx(
                                                            "absolute inset-0 rounded-lg bg-alsero-first group-hover:opacity-20 transition-opacity duration-300 ease-in-out pointer-events-none",
                                                            (openMenuId === link.id || isActive) ? "opacity-20" : "opacity-0"
                                                        )}
                                                    />
                                                </button>
                                            )}
                                            <AnimatePresence>
                                                {openMenuId === link.id && (
                                                    <motion.div
                                                        key={`simple-menu-${link.id}`}
                                                        initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: -6 }}
                                                        animate={prefersReduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
                                                        exit={prefersReduced ? { opacity: 0 } : { opacity: 0, y: -4 }}
                                                        transition={{ duration: prefersReduced ? 0.001 : 0.18, ease: [0.22, 1, 0.36, 1] }}
                                                        onMouseEnter={handleMenuContainerEnter}
                                                        onMouseLeave={handleMenuContainerLeave}
                                                        className="absolute left-1/2 top-full z-[130] mt-2 min-w-[240px] -translate-x-1/2"
                                                    >
                                                        {/* Gap'i kapatmak için invisible bridge element */}
                                                        <div 
                                                            className="absolute left-0 right-0 -top-2 h-2 pointer-events-auto"
                                                            onMouseEnter={handleMenuContainerEnter}
                                                        />
                                                        <div className="bg-white rounded-lg shadow-xl border border-gray-100 py-2 overflow-visible">
                                                            {link.subItems.map((subItem, idx) => (
                                                                <motion.div
                                                                    key={subItem.id}
                                                                    initial={prefersReduced ? {opacity: 0} : {opacity: 0, x: -10}}
                                                                    animate={prefersReduced ? {opacity: 1} : {opacity: 1, x: 0}}
                                                                    transition={{ duration: prefersReduced ? 0.001 : 0.12, ease: [0.22,1,0.36,1], delay: idx * 0.04 }}
                                                                    className="relative group/sub"
                                                                >
                                                                    {subItem.subItems && subItem.subItems.length > 0 ? (
                                                                        <>
                                                                            <div className="flex items-center justify-between px-6 py-3 text-md font-medium text-alsero-text-dark hover:bg-gray-50 hover:text-alsero-first transition-all duration-200 border-l-4 border-transparent hover:border-alsero-first">
                                                                                {subItem.href ? (
                                                                                    <Link href={subItem.href} className="flex-1 min-w-0">
                                                                                        {subItem.label}
                                                                                    </Link>
                                                                                ) : (
                                                                                    <span className="flex-1 min-w-0">{subItem.label}</span>
                                                                                )}
                                                                                <svg width="8" height="5" viewBox="0 0 8 5" className="-rotate-90 fill-alsero-text-dark ml-2 shrink-0" aria-hidden="true">
                                                                                    <path d="M7.87213 0.417894L7.61008 0.138679C7.52761 0.0514798 7.41788 0.00344123 7.30061 0.00344123C7.18341 0.00344123 7.07355 0.0514798 6.99108 0.138679L4.00179 3.30193L1.00905 0.135169C0.926707 0.0479698 0.816852 0 0.699648 0C0.582444 0 0.472524 0.0479698 0.390117 0.135169L0.128001 0.412663C-0.042667 0.593118 -0.042667 0.887061 0.128001 1.06752L3.69122 4.85148C3.77356 4.93861 3.88328 5 4.00153 5H4.00289C4.12016 5 4.22989 4.93854 4.31223 4.85148L7.87213 1.07777C7.9546 0.99064 7.99987 0.871025 8 0.747006C8 0.622918 7.9546 0.504955 7.87213 0.417894Z" />
                                                                                </svg>
                                                                            </div>
                                                                            <div className="invisible absolute left-full top-0 z-[130] ml-1 min-w-[220px] rounded-lg border border-gray-100 bg-white py-2 opacity-0 shadow-xl transition-all duration-150 group-hover/sub:visible group-hover/sub:opacity-100">
                                                                                {subItem.subItems.map((level2) => (
                                                                                    <Link
                                                                                        key={level2.id}
                                                                                        href={level2.href || '/'}
                                                                                        className="block px-4 py-2.5 text-sm font-medium text-alsero-text-dark hover:bg-gray-50 hover:text-alsero-first"
                                                                                    >
                                                                                        {level2.label}
                                                                                    </Link>
                                                                                ))}
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <Link
                                                                            href={subItem.href || '/'}
                                                                            className="block px-6 py-3 text-md font-medium text-alsero-text-dark hover:bg-gray-50 hover:text-alsero-first transition-all duration-200 border-l-4 border-transparent hover:border-alsero-first"
                                                                        >
                                                                            {subItem.label}
                                                                        </Link>
                                                                    )}
                                                                </motion.div>
                                                            ))}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    )
                                ) : (
                                    <Link
                                        key={`${link.id}-${menuIndex}`}
                                        href={link.href || '/'}
                                        className={clsx(
                                            "font-medium text-sm py-2 px-3 rounded-lg transition-all ease-in-out relative group inline-flex shrink-0 whitespace-nowrap xl:text-[15px] xl:px-4",
                                            isActive ? "text-alsero-first" : "text-alsero-text-dark hover:text-alsero-first"
                                        )}
                                    >
                                        <span className="relative z-10">{link.label}</span>
                                        <div
                                            className={clsx(
                                                "absolute inset-0 rounded-lg bg-alsero-first group-hover:opacity-20 transition-opacity duration-300 ease-in-out pointer-events-none",
                                                isActive ? "opacity-20" : "opacity-0"
                                            )}
                                        />
                                    </Link>
                                );
                            })}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-alsero-text-dark">
                                <span className="text-sm">{getVariable('header_menu_not_found', 'Menu not found')}</span>
                            </div>
                        )}
                    </nav>

                    <div className="flex shrink-0 items-center justify-self-end gap-2 sm:gap-3 lg:gap-4">
                        <div className="hidden lg:flex lg:items-center">
                            <ProfileMenuWrapper pageUrls={authPageUrls} initialCustomer={initialCustomer} noSessionOnServer={noSessionOnServer} />
                        </div>
                        <motion.button
                            type="button"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="relative flex h-11 w-11 shrink-0 flex-col items-center justify-center gap-1.5 rounded-xl border border-gray-200/80 bg-gradient-to-br from-gray-50 to-white p-2 shadow-sm transition-all duration-300 hover:border-alsero-first/30 hover:shadow-md group sm:h-12 sm:w-12 sm:p-2.5 lg:hidden"
                            aria-label="Toggle mobile menu"
                            aria-expanded={mobileMenuOpen}
                        >
                            <motion.span
                                className="w-6 h-0.5 bg-gradient-to-r from-alsero-text-dark to-alsero-text-dark rounded-full group-hover:from-alsero-first group-hover:to-alsero-first transition-all duration-300"
                                animate={mobileMenuOpen ? { rotate: 45, y: 6, backgroundColor: '#E31E24' } : { rotate: 0, y: 0 }}
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                            />
                            <motion.span
                                className="w-6 h-0.5 bg-gradient-to-r from-alsero-text-dark to-alsero-text-dark rounded-full group-hover:from-alsero-first group-hover:to-alsero-first transition-all duration-300"
                                animate={mobileMenuOpen ? { opacity: 0, scale: 0 } : { opacity: 1, scale: 1 }}
                                transition={{ duration: 0.2 }}
                            />
                            <motion.span
                                className="w-6 h-0.5 bg-gradient-to-r from-alsero-text-dark to-alsero-text-dark rounded-full group-hover:from-alsero-first group-hover:to-alsero-first transition-all duration-300"
                                animate={mobileMenuOpen ? { rotate: -45, y: -6, backgroundColor: '#E31E24' } : { rotate: 0, y: 0 }}
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                            />
                        </motion.button>
                    </div>
                </div>

                    {/* Mobil menü: body’ye portal — grid dışında (grid hücresi kaplamasın) */}
                    {mobileNavMounted
                        ? createPortal(
                              <AnimatePresence>
                                  {mobileMenuOpen && (
                                      <>
                                          <motion.div
                                              initial={{ opacity: 0 }}
                                              animate={{ opacity: 1 }}
                                              exit={{ opacity: 0 }}
                                              transition={{ duration: 0.3 }}
                                              className="fixed inset-0 z-[10030] bg-alsero-text-dark/55 backdrop-blur-md"
                                              onClick={() => setMobileMenuOpen(false)}
                                              aria-hidden
                                          />
                                          <motion.div
                                              initial={{ x: '-100%', opacity: 0 }}
                                              animate={{ x: 0, opacity: 1 }}
                                              exit={{ x: '-100%', opacity: 0 }}
                                              transition={{
                                                  type: "spring",
                                                  damping: 32,
                                                  stiffness: 320,
                                                  opacity: { duration: 0.2 },
                                              }}
                                              className="fixed top-0 left-0 z-[10031] flex h-[100dvh] w-[min(100vw-1rem,24rem)] max-w-[min(100vw-1rem,32rem)] flex-col overflow-hidden border-r border-alsero-text-dark/10 bg-[linear-gradient(180deg,#fdfcfa_0%,#ffffff_45%,#f6f4f1_100%)] shadow-[8px_0_40px_-12px_rgba(27,31,28,0.18)]"
                                              role="dialog"
                                              aria-modal="true"
                                              aria-labelledby="mobile-nav-dialog-title"
                                          >
                                              <div className="pointer-events-none absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-alsero-first via-alsero-first/80 to-alsero-first/30" aria-hidden />
                                              <div className="sticky top-0 z-10 border-b-2 border-alsero-first/15 bg-white/90 backdrop-blur-md">
                                                  <div className="px-5 pb-4 pt-5">
                                                      <h2 id="mobile-nav-dialog-title" className="sr-only">
                                                          {getVariable("header_mobile_menu_label", "Site menüsü")}
                                                      </h2>
                                                      <div className="flex items-start justify-between gap-3">
                                                          <Link
                                                              href="/"
                                                              onClick={() => setMobileMenuOpen(false)}
                                                              className="group min-w-0"
                                                          >
                                                              <motion.img
                                                                  src={resolveMediaUrl("/static/alsero-logo.png")}
                                                                  alt="alsero-logo"
                                                                  width={200}
                                                                  height={36}
                                                                  loading="lazy"
                                                                  decoding="async"
                                                                  className="h-auto max-w-[min(100%,200px)] transition-transform duration-300 group-hover:scale-[1.02]"
                                                                  whileHover={{ scale: 1.02 }}
                                                              />
                                                              <span className="mt-1.5 block w-full max-w-[min(100%,200px)] text-left text-xs font-medium leading-snug tracking-wide text-alsero-text-dark">
                                                                  {logoTagline || getVariable("header_mobile_menu_tagline", "Premium journeys")}
                                                              </span>
                                                          </Link>
                                                          <motion.button
                                                              type="button"
                                                              onClick={() => setMobileMenuOpen(false)}
                                                              whileHover={{ scale: 1.05 }}
                                                              whileTap={{ scale: 0.95 }}
                                                              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-alsero-first/35 bg-white text-alsero-first shadow-sm transition-colors hover:bg-alsero-first hover:text-white"
                                                              aria-label="Close mobile menu"
                                                          >
                                                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                                                  <path
                                                                      d="M18 6L6 18M6 6L18 18"
                                                                      stroke="currentColor"
                                                                      strokeWidth="2.5"
                                                                      strokeLinecap="round"
                                                                      strokeLinejoin="round"
                                                                  />
                                                              </svg>
                                                          </motion.button>
                                                      </div>
                                                  </div>
                                              </div>
                                              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                                    
                                    <div className="p-5 pt-3">
                                        {(headerTopLinks.length > 0 || headerSocialLinks.length > 0) && (
                                            <div className="mb-4 space-y-4 border-b border-alsero-text-dark/10 pb-4 md:hidden">
                                                {headerTopLinks.length > 0 ? (
                                                    <nav aria-label={getVariable('header_top_links_label', 'Quick links')}>
                                                        <ul className="flex flex-col gap-0.5" role="list">
                                                            {headerTopLinks.map((item, index) => {
                                                                const href = (item.href || item.link || '#').trim();
                                                                const label = item.label || item.text || item.title || '';
                                                                const isInternal = href.startsWith('/') && !href.startsWith('//');
                                                                return (
                                                                    <li key={`${href}-${index}`} role="listitem">
                                                                        {isInternal ? (
                                                                            <Link
                                                                                href={href}
                                                                                onClick={() => setMobileMenuOpen(false)}
                                                                                className="block py-2.5 text-sm font-medium text-alsero-text-dark hover:text-alsero-first"
                                                                            >
                                                                                {label}
                                                                            </Link>
                                                                        ) : (
                                                                            <a
                                                                                href={href}
                                                                                target={href.startsWith('http') ? '_blank' : undefined}
                                                                                rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                                                                onClick={() => setMobileMenuOpen(false)}
                                                                                className="block py-2.5 text-sm font-medium text-alsero-text-dark hover:text-alsero-first"
                                                                            >
                                                                                {label}
                                                                            </a>
                                                                        )}
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </nav>
                                                ) : null}
                                                {headerSocialLinks.length > 0 ? (
                                                    <ul className="flex flex-wrap items-center gap-3" role="list" aria-label={getVariable('header_social_label', 'Social media')}>
                                                        {headerSocialLinks.map((item, index) => (
                                                            <li key={`${item.href}-${index}`} role="listitem">
                                                                <a
                                                                    href={item.href || '#'}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    aria-label={item.title || item.label || 'Social link'}
                                                                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-alsero-first/25 bg-alsero-first/5 text-alsero-first transition-colors hover:bg-alsero-first hover:text-white"
                                                                >
                                                                    {item.svg ? (
                                                                        <span
                                                                            className="inline-flex h-4 w-4 items-center justify-center [&_svg]:h-4 [&_svg]:w-4"
                                                                            dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(item.svg) }}
                                                                            aria-hidden
                                                                        />
                                                                    ) : (
                                                                        <span className="text-xs font-bold uppercase">{item.icon.slice(0, 1)}</span>
                                                                    )}
                                                                </a>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : null}
                                            </div>
                                        )}

                                        {menuLoading ? (
                                            <div className="flex items-center justify-center gap-3 py-12">
                                                <motion.div 
                                                    className="w-6 h-6 border-3 border-alsero-first border-t-transparent rounded-full"
                                                    animate={{ rotate: 360 }}
                                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                />
                                                <span className="text-sm font-medium text-alsero-text-dark">{getVariable('header_menu_loading', 'Loading menu...')}</span>
                                            </div>
                                        ) : menuError ? (
                                            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="#EF4444" strokeWidth="2"/>
                                                    <path d="M10 6V10M10 14H10.01" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                                                </svg>
                                                <span className="text-sm font-medium text-red-700">{menuError}</span>
                                            </div>
                                        ) : menuItems && menuItems.length > 0 ? (
                                            <nav
                                                className="-mx-1 flex flex-col border-t border-alsero-text-dark/10"
                                                aria-label="Mobile navigation"
                                            >
                                                {menuItems.map((link, index) => {
                                                    const isExpanded = expandedMobileMenuIds.has(link.id);
                                                    
                                                    const toggleMenu = (menuId: string) => {
                                                        setExpandedMobileMenuIds(prev => {
                                                            const newSet = new Set(prev);
                                                            if (newSet.has(menuId)) {
                                                                newSet.delete(menuId);
                                                            } else {
                                                                newSet.add(menuId);
                                                            }
                                                            return newSet;
                                                        });
                                                    };
                                                    
                                                    return (
                                                        <motion.div
                                                            key={`${link.id}-${index}`}
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: index * 0.05, duration: 0.3 }}
                                                            className="group"
                                                        >
                                                            {link.subItems && link.subItems.length > 0 ? (
                                                                <div className="border-b border-alsero-text-dark/10">
                                                                    <motion.button
                                                                        type="button"
                                                                        onClick={() => toggleMenu(link.id)}
                                                                        whileTap={{ scale: 0.99 }}
                                                                        className="group flex w-full items-center justify-between px-1 py-4 text-left transition-colors hover:bg-alsero-first/[0.04]"
                                                                    >
                                                                        <div className="flex min-w-0 flex-1 items-center gap-3">
                                                                            <span
                                                                                className={clsx(
                                                                                    "h-8 w-0.5 shrink-0 rounded-full transition-colors",
                                                                                    isExpanded
                                                                                        ? "bg-alsero-first"
                                                                                        : "bg-alsero-first/25 group-hover:bg-alsero-first",
                                                                                )}
                                                                                aria-hidden
                                                                            />
                                                                            <span className="font-semibold text-[1.05rem] leading-snug tracking-tight text-alsero-text-dark transition-colors group-hover:text-alsero-first">
                                                                                {link.label}
                                                                            </span>
                                                                        </div>
                                                                        <motion.div
                                                                            animate={{ rotate: isExpanded ? 180 : 0 }}
                                                                            transition={{ duration: 0.3, ease: "easeInOut" }}
                                                                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-alsero-text-dark/10 bg-alsero-soft-white/90 text-alsero-text-dark transition-colors group-hover:border-alsero-first/35 group-hover:text-alsero-first"
                                                                        >
                                                                            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-alsero-text-dark group-hover:text-alsero-first transition-colors"/>
                                                                            </svg>
                                                                        </motion.div>
                                                                    </motion.button>
                                                                    <AnimatePresence>
                                                                        {isExpanded && (
                                                                            <motion.div
                                                                                initial={{ height: 0, opacity: 0 }}
                                                                                animate={{ height: 'auto', opacity: 1 }}
                                                                                exit={{ height: 0, opacity: 0 }}
                                                                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                                                                className="overflow-hidden"
                                                                            >
                                                                                <div className="ml-2 border-l border-dotted border-alsero-text-dark/15 py-1 pl-3 pr-1">
                                                                                    {link.subItems.map((subItem, subIndex) => {
                                                                                        const isSubExpanded = expandedMobileMenuIds.has(subItem.id);
                                                                                        return subItem.subItems && subItem.subItems.length > 0 ? (
                                                                                            <motion.div
                                                                                                key={subItem.id}
                                                                                                initial={{ opacity: 0, x: -10 }}
                                                                                                animate={{ opacity: 1, x: 0 }}
                                                                                                transition={{ delay: subIndex * 0.03 }}
                                                                                                className="border-t border-alsero-text-dark/[0.06] first:border-t-0"
                                                                                            >
                                                                                                <motion.button
                                                                                                    type="button"
                                                                                                    onClick={() => toggleMenu(subItem.id)}
                                                                                                    whileTap={{ scale: 0.99 }}
                                                                                                    className="flex w-full items-center justify-between py-3 pl-3 pr-1 text-left text-sm font-medium text-alsero-text-dark transition-colors hover:text-alsero-first"
                                                                                                >
                                                                                                    <span className="min-w-0">{subItem.label}</span>
                                                                                                    <motion.span
                                                                                                        animate={{ rotate: isSubExpanded ? 90 : 0 }}
                                                                                                        transition={{ duration: 0.2 }}
                                                                                                        className="flex h-7 w-7 shrink-0 items-center justify-center text-alsero-first/80"
                                                                                                        aria-hidden
                                                                                                    >
                                                                                                        <svg width="10" height="10" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                                            <path d="M3 1L7 4L3 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                                        </svg>
                                                                                                    </motion.span>
                                                                                                </motion.button>
                                                                                                <AnimatePresence>
                                                                                                    {isSubExpanded && (
                                                                                                        <motion.div
                                                                                                            initial={{ height: 0, opacity: 0 }}
                                                                                                            animate={{ height: 'auto', opacity: 1 }}
                                                                                                            exit={{ height: 0, opacity: 0 }}
                                                                                                            transition={{ duration: 0.25 }}
                                                                                                            className="overflow-hidden border-l-2 border-alsero-first/15 bg-alsero-soft-white/30 pl-1"
                                                                                                        >
                                                                                                            <div className="space-y-0.5 py-1 pr-1">
                                                                                                                {subItem.subItems.map((level2Item, level2Index) => (
                                                                                                                    <motion.div
                                                                                                                        key={level2Item.id}
                                                                                                                        initial={{ opacity: 0, x: -5 }}
                                                                                                                        animate={{ opacity: 1, x: 0 }}
                                                                                                                        transition={{ delay: level2Index * 0.02 }}
                                                                                                                    >
                                                                                                                        <Link
                                                                                                                            href={level2Item.href!}
                                                                                                                            onClick={() => setMobileMenuOpen(false)}
                                                                                                                            className="block py-2 pl-3 text-sm text-alsero-text-dark transition-colors hover:text-alsero-first"
                                                                                                                        >
                                                                                                                            {level2Item.label}
                                                                                                                        </Link>
                                                                                                                    </motion.div>
                                                                                                                ))}
                                                                                                            </div>
                                                                                                        </motion.div>
                                                                                                    )}
                                                                                                </AnimatePresence>
                                                                                            </motion.div>
                                                                                        ) : (
                                                                                            <motion.div
                                                                                                key={subItem.id}
                                                                                                initial={{ opacity: 0, x: -10 }}
                                                                                                animate={{ opacity: 1, x: 0 }}
                                                                                                transition={{ delay: subIndex * 0.03 }}
                                                                                            >
                                                                                                <Link
                                                                                                    href={subItem.href!}
                                                                                                    onClick={() => setMobileMenuOpen(false)}
                                                                                                    className="block py-3 pl-3 text-sm font-medium text-alsero-text-dark transition-colors hover:text-alsero-first"
                                                                                                >
                                                                                                    {subItem.label}
                                                                                                </Link>
                                                                                            </motion.div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </motion.div>
                                                                        )}
                                                                    </AnimatePresence>
                                                                </div>
                                                            ) : (
                                                                <div className="border-b border-alsero-text-dark/10">
                                                                    <Link
                                                                        href={link.href!}
                                                                        onClick={() => setMobileMenuOpen(false)}
                                                                        className="group flex items-center gap-3 px-1 py-4 font-semibold text-[1.05rem] leading-snug tracking-tight text-alsero-text-dark transition-colors hover:text-alsero-first"
                                                                    >
                                                                        <span className="h-8 w-0.5 shrink-0 rounded-full bg-alsero-first/20 transition-colors group-hover:bg-alsero-first" aria-hidden />
                                                                        {link.label}
                                                                    </Link>
                                                                </div>
                                                            )}
                                                        </motion.div>
                                                    );
                                                })}
                                            </nav>
                                        ) : (
                                            <div className="flex items-center justify-center gap-3 py-12">
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                <span className="text-sm font-medium text-gray-500">{getVariable('header_menu_not_found', 'Menu not found')}</span>
                                            </div>
                                        )}
                                        
                                        {/* Mobile Menu Footer - Premium Design */}
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2, duration: 0.4 }}
                                            className="mt-8 border-t border-alsero-text-dark/10 pt-6"
                                        >
                                            {/* Profile Section - ProfileMenu kendi component'inde gösteriliyor */}
                                            {/* Mobile menu'de ProfileMenuWrapper kullanılıyor, conditional rendering gerekmiyor */}
                                            
                                            {/* Language Selector Card */}
                                            {languages && languages.length > 0 && selectedLang && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.25, duration: 0.4 }}
                                                    className="mt-2 border-t border-alsero-text-dark/10 pt-4 md:hidden"
                                                >
                                                    <div className="overflow-hidden">
                                                        <motion.button
                                                            type="button"
                                                            onClick={() => setMobileLanguageOpen(!mobileLanguageOpen)}
                                                            className="flex w-full items-center justify-between gap-3 py-2 text-left transition-colors hover:text-alsero-first"
                                                        >
                                                            <div className="flex min-w-0 flex-1 items-center gap-2.5">
                                                                <div className="flex h-8 w-8 shrink-0 items-center justify-center text-alsero-first">
                                                                    {selectedLang.attributes.svg_code ? (
                                                                        <div
                                                                            className="w-6 h-6"
                                                                            dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(selectedLang.attributes.svg_code) }}
                                                                        />
                                                                    ) : (
                                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-alsero-first">
                                                                            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                                <div className="flex-1">
                                                                    <div className="text-xs font-bold text-alsero-first uppercase tracking-wider mb-0.5">
                                                                        {getVariable('header_menu_language', 'Language')}
                                                                    </div>
                                                                    <div className="text-sm font-semibold text-alsero-text-dark">
                                                                        {selectedLang.attributes.name}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <motion.span
                                                                animate={{ rotate: mobileLanguageOpen ? 180 : 0 }}
                                                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                                                className="flex h-7 w-7 shrink-0 items-center justify-center text-alsero-first/80"
                                                                aria-hidden
                                                            >
                                                                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                            </motion.span>
                                                        </motion.button>
                                                        <AnimatePresence>
                                                            {mobileLanguageOpen && (
                                                                <motion.div
                                                                    initial={{ height: 0, opacity: 0 }}
                                                                    animate={{ height: 'auto', opacity: 1 }}
                                                                    exit={{ height: 0, opacity: 0 }}
                                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                                    className="overflow-hidden border-t border-alsero-text-dark/10"
                                                                >
                                                                    <div className="space-y-0 py-1">
                                                                        {languages.map((lang, index) => (
                                                                            <motion.button
                                                                                type="button"
                                                                                key={lang.attributes.code}
                                                                                onClick={() => handleLanguageChange(lang)}
                                                                                initial={{ opacity: 0, x: -10 }}
                                                                                animate={{ opacity: 1, x: 0 }}
                                                                                transition={{ delay: index * 0.03 }}
                                                                                className={clsx(
                                                                                    "flex w-full items-center gap-2.5 py-2.5 pl-1 pr-2 text-left text-sm transition-colors",
                                                                                    selectedLangCode === lang.attributes.code
                                                                                        ? "font-semibold text-alsero-first"
                                                                                        : "text-alsero-text-dark hover:text-alsero-first"
                                                                                )}
                                                                            >
                                                                                <div className="flex h-7 w-7 shrink-0 items-center justify-center">
                                                                                    {lang.attributes.svg_code ? (
                                                                                        <div
                                                                                            className="w-5 h-5"
                                                                                            dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(lang.attributes.svg_code) }}
                                                                                        />
                                                                                    ) : (
                                                                                        <div className="w-5 h-5 bg-gray-300 rounded" />
                                                                                    )}
                                                                                </div>
                                                                                <span className="text-sm font-medium flex-1">{lang.attributes.name}</span>
                                                                                {selectedLangCode === lang.attributes.code && (
                                                                                    <motion.svg
                                                                                        initial={{ scale: 0, rotate: -180 }}
                                                                                        animate={{ scale: 1, rotate: 0 }}
                                                                                        transition={{ duration: 0.3, ease: "backOut" }}
                                                                                        width="16"
                                                                                        height="16"
                                                                                        viewBox="0 0 16 16"
                                                                                        fill="none"
                                                                                        className="text-alsero-first"
                                                                                    >
                                                                                        <path
                                                                                            d="M13.3337 4L6.00033 11.3333L2.66699 8"
                                                                                            stroke="currentColor"
                                                                                            strokeWidth="2"
                                                                                            strokeLinecap="round"
                                                                                            strokeLinejoin="round"
                                                                                        />
                                                                                    </motion.svg>
                                                                                )}
                                                                            </motion.button>
                                                                        ))}
                                                                    </div>
                                                                </motion.div>
                                                            )}
                                                        </AnimatePresence>
                                                    </div>
                                                </motion.div>
                                            )}
                                            
                                            {/* Contact Information Section */}
                                            {contactInfo && (contactInfo.address || contactInfo.email || contactInfo.phone) && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.3, duration: 0.4 }}
                                                    className="mt-6 space-y-5 border-t border-alsero-text-dark/10 pt-5 text-sm md:hidden"
                                                >
                                                    {contactInfo.address && (
                                                        <div>
                                                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-alsero-first">
                                                                {getVariable('header_menu_address', 'Address')}
                                                            </div>
                                                            <p className="leading-relaxed text-alsero-text-dark">{contactInfo.address}</p>
                                                        </div>
                                                    )}
                                                    
                                                    {contactInfo.email && (
                                                        <div>
                                                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-alsero-first">
                                                                {getVariable('header_menu_email', 'Email')}
                                                            </div>
                                                            <Link
                                                                href={contactInfo.email.startsWith("mailto:") ? contactInfo.email : `mailto:${contactInfo.email}`}
                                                                onClick={() => setMobileMenuOpen(false)}
                                                                className="break-all font-medium text-alsero-text-dark underline-offset-2 hover:text-alsero-first hover:underline"
                                                            >
                                                                {contactInfo.email.replace(/^mailto:/, "")}
                                                            </Link>
                                                        </div>
                                                    )}
                                                    
                                                    {contactInfo.phone && (
                                                        <div>
                                                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-alsero-first">
                                                                {getVariable('header_menu_phone', 'Phone')}
                                                            </div>
                                                            <Link
                                                                href={contactInfo.phone.startsWith("tel:") ? contactInfo.phone : `tel:${contactInfo.phone}`}
                                                                onClick={() => setMobileMenuOpen(false)}
                                                                className="inline-flex items-center gap-2 font-semibold text-alsero-text-dark hover:text-alsero-first"
                                                            >
                                                                <span>{contactInfo.phone.replace(/^tel:/, "")}</span>
                                                                <span className="text-alsero-first/70" aria-hidden>
                                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M4 2L16 10L4 18V2Z" fill="currentColor"/>
                                                                    </svg>
                                                                </span>
                                                            </Link>
                                                        </div>
                                                    )}
                                                </motion.div>
                                            )}
                                        </motion.div>
                                    </div>
                                              </div>
                                          </motion.div>
                                      </>
                                  )}
                              </AnimatePresence>,
                              document.body,
                          )
                        : null}

                <AnimatePresence>
                    {activeMegaMenuLink && openMenuId === activeMegaMenuLink.id && (
                        <motion.div
                            key={`menu-${activeMegaMenuLink.id}`}
                            initial={prefersReduced ? { opacity: 0 } : { opacity: 0, scaleY: 0.98, transformOrigin: 'top center' }}
                            animate={prefersReduced ? { opacity: 1 } : { opacity: 1, scaleY: 1 }}
                            exit={prefersReduced ? { opacity: 0 } : { opacity: 0, scaleY: 0.985 }}
                            transition={{ duration: prefersReduced ? 0.001 : 0.18, ease: [0.22, 1, 0.36, 1] }}
                            onMouseEnter={handleMenuContainerEnter}
                            onMouseLeave={handleMenuContainerLeave}
                            className="pointer-events-none absolute inset-x-0 top-full z-[120] hidden will-change-transform will-change-opacity lg:block"
                            style={{ transform: 'translateZ(0)' }}
                        >
                            <div
                                className="absolute inset-x-0 -top-2 h-2 pointer-events-auto"
                                onMouseEnter={handleMenuContainerEnter}
                            />
                            <div className="pointer-events-auto mx-auto max-w-[min(92vw,1280px)] rounded-b-2xl bg-white shadow-2xl 2xl:max-w-[min(96vw,1536px)]">
                                <div className="flex">
                                    <div className="w-64 border-r border-gray-200 bg-gray-50 py-4">
                                        <div className="space-y-1">
                                            {activeMegaMenuLink.subItems?.map((subItem, idx) => (
                                                <motion.div
                                                    key={subItem.id}
                                                    initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: -6 }}
                                                    animate={prefersReduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
                                                    transition={{ duration: prefersReduced ? 0.001 : 0.12, ease: [0.22, 1, 0.36, 1], delay: idx * 0.02 }}
                                                    onMouseEnter={() => {
                                                        if (subMenuCloseTimer.current) {
                                                            clearTimeout(subMenuCloseTimer.current);
                                                        }
                                                        handleSubMenuEnter(subItem.id);
                                                    }}
                                                >
                                                    {subItem.subItems && subItem.subItems.length > 0 ? (
                                                        subItem.href ? (
                                                            <Link
                                                                href={subItem.href}
                                                                className={clsx(
                                                                    'flex items-center justify-between px-6 py-3 text-md font-medium transition-all duration-200',
                                                                    openSubMenuId === subItem.id
                                                                        ? 'border-l-4 border-alsero-first bg-white text-alsero-first'
                                                                        : 'text-alsero-text-dark hover:border-l-4 hover:border-alsero-first/30 hover:bg-white hover:text-alsero-first',
                                                                )}
                                                            >
                                                                <span>{subItem.label}</span>
                                                                <svg width="8" height="5" viewBox="0 0 8 5" className={clsx('-rotate-90 transition-all duration-200', openSubMenuId === subItem.id ? 'fill-alsero-first' : 'fill-alsero-text-dark')} aria-hidden="true" focusable="false">
                                                                    <path d="M7.87213 0.417894L7.61008 0.138679C7.52761 0.0514798 7.41788 0.00344123 7.30061 0.00344123C7.18341 0.00344123 7.07355 0.0514798 6.99108 0.138679L4.00179 3.30193L1.00905 0.135169C0.926707 0.0479698 0.816852 0 0.699648 0C0.582444 0 0.472524 0.0479698 0.390117 0.135169L0.128001 0.412663C-0.042667 0.593118 -0.042667 0.887061 0.128001 1.06752L3.69122 4.85148C3.77356 4.93861 3.88328 5 4.00153 5H4.00289C4.12016 5 4.22989 4.93854 4.31223 4.85148L7.87213 1.07777C7.9546 0.99064 7.99987 0.871025 8 0.747006C8 0.622918 7.9546 0.504955 7.87213 0.417894Z" />
                                                                </svg>
                                                            </Link>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className={clsx(
                                                                    'flex w-full items-center justify-between px-6 py-3 text-left text-md font-medium transition-all duration-200',
                                                                    openSubMenuId === subItem.id
                                                                        ? 'border-l-4 border-alsero-first bg-white text-alsero-first'
                                                                        : 'text-alsero-text-dark hover:border-l-4 hover:border-alsero-first/30 hover:bg-white hover:text-alsero-first',
                                                                )}
                                                            >
                                                                <span>{subItem.label}</span>
                                                                <svg width="8" height="5" viewBox="0 0 8 5" className={clsx('-rotate-90 transition-all duration-200', openSubMenuId === subItem.id ? 'fill-alsero-first' : 'fill-alsero-text-dark')} aria-hidden="true" focusable="false">
                                                                    <path d="M7.87213 0.417894L7.61008 0.138679C7.52761 0.0514798 7.41788 0.00344123 7.30061 0.00344123C7.18341 0.00344123 7.07355 0.0514798 6.99108 0.138679L4.00179 3.30193L1.00905 0.135169C0.926707 0.0479698 0.816852 0 0.699648 0C0.582444 0 0.472524 0.0479698 0.390117 0.135169L0.128001 0.412663C-0.042667 0.593118 -0.042667 0.887061 0.128001 1.06752L3.69122 4.85148C3.77356 4.93861 3.88328 5 4.00153 5H4.00289C4.12016 5 4.22989 4.93854 4.31223 4.85148L7.87213 1.07777C7.9546 0.99064 7.99987 0.871025 8 0.747006C8 0.622918 7.9546 0.504955 7.87213 0.417894Z" />
                                                                </svg>
                                                            </button>
                                                        )
                                                    ) : (
                                                        <Link
                                                            href={subItem.href!}
                                                            className="block px-6 py-3 text-md font-medium text-alsero-text-dark transition-all duration-200 hover:border-l-4 hover:border-alsero-first/30 hover:bg-white hover:text-alsero-first"
                                                        >
                                                            {subItem.label}
                                                        </Link>
                                                    )}
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                    <AnimatePresence mode="wait">
                                        {openSubMenuId && activeMegaSubMenu ? (
                                            <motion.div
                                                key={`submenu-container-${openSubMenuId}`}
                                                className="flex-1 overflow-hidden will-change-transform will-change-opacity"
                                                initial={prefersReduced ? { opacity: 0 } : { opacity: 0, x: 14 }}
                                                animate={prefersReduced ? { opacity: 1 } : { opacity: 1, x: 0 }}
                                                exit={prefersReduced ? { opacity: 0 } : { opacity: 0, x: 12 }}
                                                transition={{ duration: prefersReduced ? 0.001 : 0.22, ease: [0.22, 1, 0.36, 1] }}
                                                style={{ transform: 'translateZ(0)' }}
                                            >
                                                <div className="mega-menu-scroll max-h-[calc(100vh-200px)] overflow-y-auto px-8 py-6">
                                                    {(() => {
                                                        const activeSub = activeMegaSubMenu;
                                                        const activeSubStyle = activeSub?.menuStyle ?? 'auto';
                                                        const effectiveStyle = activeSubStyle !== 'auto' ? activeSubStyle : activeMegaMenuStyle;
                                                        const useNestedDropdown = effectiveStyle === 'dropdown';
                                                        const hasSubCategories =
                                                            activeSub.subItems && activeSub.subItems.length > 0;
                                                        const featuredProducts =
                                                            featuredProductsBySubMenu[activeSub.id] ?? [];
                                                        const featuredLoading =
                                                            featuredLoadingSubMenuId === activeSub.id;

                                                        return (
                                                            <>
                                                                <h3 className="mb-6 text-2xl font-bold text-alsero-text-dark">{activeSub?.label}</h3>
                                                                {hasSubCategories ? (
                                                                    useNestedDropdown ? (
                                                                        <div className="grid grid-cols-3 gap-x-8 gap-y-2">
                                                                            {activeSub?.subItems?.map((level2Item, idx) => (
                                                                                <motion.div
                                                                                    key={level2Item.id}
                                                                                    initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 6 }}
                                                                                    animate={prefersReduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
                                                                                    transition={{ duration: prefersReduced ? 0.001 : 0.12, ease: [0.22, 1, 0.36, 1], delay: idx * 0.01 }}
                                                                                >
                                                                                    <Link href={level2Item.href || '/'} className="block rounded-md px-3 py-2 text-sm font-semibold text-alsero-text-dark transition-colors hover:bg-gray-50 hover:text-alsero-first">
                                                                                        {level2Item.label}
                                                                                    </Link>
                                                                                </motion.div>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="grid grid-cols-5 gap-5">
                                                                            {activeSub?.subItems?.map((level2Item, idx) => (
                                                                                <motion.div
                                                                                    key={level2Item.id}
                                                                                    initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 10 }}
                                                                                    animate={prefersReduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
                                                                                    transition={{ duration: prefersReduced ? 0.001 : 0.14, ease: [0.22, 1, 0.36, 1], delay: idx * 0.03 }}
                                                                                >
                                                                                    <Link href={level2Item.href || '/'} className="group block overflow-hidden rounded-lg border border-gray-200 bg-white transition-all duration-300 hover:border-alsero-first hover:shadow-lg">
                                                                                        <div className="relative h-32 overflow-hidden bg-gray-100">
                                                                                            {level2Item.image ? (
                                                                                                <Image src={resolveMediaUrl(level2Item.image)} alt={level2Item.label} fill className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110" unoptimized={true} />
                                                                                            ) : (
                                                                                                <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200" />
                                                                                            )}
                                                                                        </div>
                                                                                        <div className="p-4">
                                                                                            <h4 className="break-words text-sm font-semibold leading-snug text-alsero-text-dark transition-colors duration-200 group-hover:text-alsero-first">
                                                                                                {level2Item.label}
                                                                                            </h4>
                                                                                        </div>
                                                                                    </Link>
                                                                                </motion.div>
                                                                            ))}
                                                                        </div>
                                                                    )
                                                                ) : featuredLoading ? (
                                                                    <div className="flex items-center gap-2 py-8 text-alsero-text-dark">
                                                                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-alsero-first border-t-transparent" />
                                                                        <span className="text-sm">{getVariable('header_menu_loading', 'Loading...')}</span>
                                                                    </div>
                                                                ) : featuredProducts.length > 0 ? (
                                                                    <div className="grid grid-cols-3 gap-5 lg:grid-cols-4">
                                                                        {featuredProducts.map((product, idx) => {
                                                                            const href = product.url || activeSub.href || '/';
                                                                            const imageSrc = featuredProductImage(product);
                                                                            return (
                                                                                <motion.div
                                                                                    key={product.id}
                                                                                    initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 10 }}
                                                                                    animate={prefersReduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
                                                                                    transition={{ duration: prefersReduced ? 0.001 : 0.14, ease: [0.22, 1, 0.36, 1], delay: idx * 0.03 }}
                                                                                >
                                                                                    <Link href={href} className="group block overflow-hidden rounded-lg border border-gray-200 bg-white transition-all duration-300 hover:border-alsero-first hover:shadow-lg">
                                                                                        <div className="relative h-32 overflow-hidden bg-gray-100">
                                                                                            {imageSrc ? (
                                                                                                <Image src={resolveMediaUrl(imageSrc)} alt={product.name} fill className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110" unoptimized={true} />
                                                                                            ) : (
                                                                                                <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200" />
                                                                                            )}
                                                                                        </div>
                                                                                        <div className="p-4">
                                                                                            <h4 className="break-words text-sm font-semibold leading-snug text-alsero-text-dark transition-colors duration-200 group-hover:text-alsero-first">
                                                                                                {product.name}
                                                                                            </h4>
                                                                                        </div>
                                                                                    </Link>
                                                                                </motion.div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-sm text-gray-500">
                                                                        {getVariable('header_menu_no_featured', 'No featured products in this category.')}
                                                                    </p>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </motion.div>
                                        ) : null}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </>
    );
}

